import React, {
  useState,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  DraftsOutlined as EmailIcon,
  SettingsPhone as CallLogIcon,
} from '@mui/icons-material';

import { ReactComponent as RecordOutcomeIcon } from '../../../../../../../../assets/icons/message-question-icon.svg';
import { ReactComponent as CallIcon } from '../../../../../../../../assets/icons/phone-outgoing-icon.svg';
import { getQueryVariable } from '../../../../../../../../utils/queryParams';
import { PrimaryButton } from '../../../../../../../../components/Button/ActionButtons';
import LoadingOverlay from '../../../../../../LoadingOverlay';
import CallLogModal from '../../../../CallLogModal';
import ReasonModal from '../../../../ReasonModal';
import CalDotComMeetingCancelModal from '../../../../CalDotComMeetingCancelModal';
import Lead, {
  LeadHistoryType,
  SalesStage,
  isLockedLeadCallOutcome,
} from '../../../../../../../Model/Lead';
import {
  createTryRazContact,
  getTryRazConversationLink,
} from '../../../../../utils';
import { rescheduleViaSDR } from '../../../../../../../utils/lead';
import FirebaseContext from '../../../../../../../../context/FirebaseContext';
import LoggedInUserContext from '../../../../../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../../../../../hooks/useComponentMounted';
import useToast from '../../../../../../../hooks/useToast';
import CallOutcomeModal from '../../../../CallOutcomeModal';

import {
  ActionButton,
  ButtonsContainer,
} from './styles';
import texts from './texts.json';

const ActionButtons = ({
  lead,
  isInsideSalesView,
  onCallSuccess,
  onRescheduleSuccess,
  userId,
  userName,
  onCloseLeadModal,
  history,
  initialOutcome,
  onOutcomeSaved,
}) => {
  const [showRescheduledCallModal, setShowRescheduledCallModal] = useState(false);
  const [showRescheduleViaSDRModal, setShowRescheduleViaSDRModal] = useState(false);
  const [showCalDotComMeetingCancelModal, setShowCalDotComMeetingCancelModal] = useState(false);
  const [showRecordOutcomesModal, setShowRecordOutcomesModal] = useState(false);
  const [showCallLogModal, setShowCallLogModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();

  const showOutreachOptions = getQueryVariable('isSuperUser') === 'true';
  const isOutcomeLocked = isLockedLeadCallOutcome(initialOutcome);

  // Check if we should show the cancel Cal.com meeting button
  const shouldShowCancelCalDotComButton = !!lead.calendlyMeetingTime
    && moment(lead.calendlyMeetingTime.toDate()).isAfter(moment());

  const onCallButtonClick = useCallback(async () => {
    setIsLoading(true);
    const { success, error } = await createTryRazContact(remote, lead.id);
    if (success) {
      // open tryraz in new tab
      window.open(getTryRazConversationLink(lead.phoneNumber), '_blank');
      await userDoc.updateIsISOnCall(false);
      if (isComponentMountedRef.current) {
        onCallSuccess();
      }
    } else {
      showToast(error || texts.somethingWentWrong, { error: true });
    }
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    isComponentMountedRef,
    lead,
    remote,
    showToast,
    onCallSuccess,
    userDoc,
  ]);

  const onRescheduleViaSDR = useCallback(async ({ reason }) => {
    await rescheduleViaSDR(lead, reason, userId, userName);
    onRescheduleSuccess();
  }, [lead, userId, userName, onRescheduleSuccess]);

  const markLeadAsNoShow = useCallback(async () => {
    // Add a new entry to the lead history to track who marked the lead.
    await lead.addItemToHistory({
      value: texts.noShowValue,
      createdAt: new Date(),
      activityType: LeadHistoryType.NO_SHOW,
      createdBy: userId,
      createdByUserName: userName,
    });

    // Set lead's no show flag.
    await lead.setNoShow();
  }, [
    lead,
    userId,
    userName,
  ]);

  return (
    <ButtonsContainer>
      {isInsideSalesView && (
        <>
          <PrimaryButton
            onClick={onCallButtonClick}
            size="medium"
            icon={<CallIcon />}
          >
            {texts.callNow}
          </PrimaryButton>
          <PrimaryButton
            onClick={() => setShowRecordOutcomesModal(true)}
            size="medium"
            variant="light"
            icon={<RecordOutcomeIcon />}
          >
            {texts.recordOutcomes}
          </PrimaryButton>
          <ActionButton
            variant="outlined"
            startIcon={<CallLogIcon />}
            provider="logACall"
            onClick={() => setShowCallLogModal(true)}
          >
            {texts.logACall}
          </ActionButton>
          {lead.salesStage === SalesStage.CALENDLY_SCHEDULED && (
            <>
              <ActionButton
                variant="outlined"
                provider="sentToReschedule" // TODO: standardize buttons to avoid using provider as type
                onClick={() => setShowRescheduleViaSDRModal(true)}
              >
                {texts.rescheduleViaSDR}
              </ActionButton>
              <ActionButton
                variant="outlined"
                provider="rescheduledManually" // TODO: standardize buttons to avoid using provider as type
                onClick={() => setShowRescheduledCallModal(true)}
              >
                {texts.rescheduledManually}
              </ActionButton>
              <ActionButton
                variant="outlined"
                provider="noShow" // TODO: standardize buttons to avoid using provider as type
                onClick={markLeadAsNoShow}
              >
                {texts.noShow}
              </ActionButton>
              {shouldShowCancelCalDotComButton && (
                <ActionButton
                  variant="outlined"
                  provider="cancelMeeting"
                  onClick={() => setShowCalDotComMeetingCancelModal(true)}
                >
                  {texts.cancelCalDotComMeeting}
                </ActionButton>
              )}
            </>
          )}
        </>
      )}
      {showOutreachOptions && (
        <ActionButton
          variant="outlined"
          startIcon={<EmailIcon />}
          provider="email"
          href={`mailto:${lead?.email}`}
        >
          {texts.sendEmail}
        </ActionButton>
      )}
      {showCallLogModal && (
        <CallLogModal
          showModal={showCallLogModal}
          onClose={() => setShowCallLogModal(false)}
          lead={lead}
          userId={userId}
          userName={userName}
        />
      )}
      {showRescheduledCallModal && (
        <CallLogModal
          showModal={showRescheduledCallModal}
          onClose={() => setShowRescheduledCallModal(false)}
          lead={lead}
          userId={userId}
          userName={userName}
          logRescheduledCall
        />
      )}
      {showRescheduleViaSDRModal && (
        <ReasonModal
          title={texts.rescheduleViaSDR}
          onSave={onRescheduleViaSDR}
          onClose={() => setShowRescheduleViaSDRModal(false)}
          showModal={showRescheduleViaSDRModal}
        />
      )}
      {showCalDotComMeetingCancelModal && (
        <CalDotComMeetingCancelModal
          leadId={lead.id}
          onClose={() => setShowCalDotComMeetingCancelModal(false)}
          setLoading={setIsLoading}
          userName={userName}
        />
      )}
      {showRecordOutcomesModal && (
        <CallOutcomeModal
          lead={lead}
          onClose={() => setShowRecordOutcomesModal(false)}
          showModal={showRecordOutcomesModal}
          onCloseLeadModal={onCloseLeadModal}
          initialOutcome={initialOutcome}
          isOutcomeLocked={isOutcomeLocked}
          onAfterSave={onOutcomeSaved}
          userDoc={userDoc}
          history={history}
        />
      )}
      <LoadingOverlay isLoading={isLoading} />
    </ButtonsContainer>
  );
};

ActionButtons.propTypes = {
  lead: PropTypes.instanceOf(Lead).isRequired,
  isInsideSalesView: PropTypes.bool.isRequired,
  onCallSuccess: PropTypes.func.isRequired,
  onRescheduleSuccess: PropTypes.func,
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  onCloseLeadModal: PropTypes.func.isRequired,
  history: PropTypes.arrayOf(PropTypes.object),
  initialOutcome: PropTypes.string,
  onOutcomeSaved: PropTypes.func,
};

ActionButtons.defaultProps = {
  history: null,
  initialOutcome: undefined,
  onOutcomeSaved: () => {},
  onRescheduleSuccess: () => {},
};

export default ActionButtons;
